import { queryOptions } from "@tanstack/react-query";
import _ from "lodash";
import { LinkSchemas } from "@/types/legal-entity/links.type";
import { AliasMailSchema } from "@/types/user/alias-mail.types";
import { AliasMobileSchema } from "@/types/user/alias-mobile.types";
import { pushOtherToEnd } from "@/utils/array.utils";
import { ApiError } from "@/utils/internal-code-handler";
import { IdentityDocumentTypeV2 } from "@/views/identity/components/level-card-status-container/verified-company-form/types";
import { CompanyPaymentInformations, companyDetails, LegalEntityFrontDto } from "../types";

export const setEmptyOrStr = (v: unknown) => {
  if (_.isString(v) && _.isEmpty(v)) return null;
  else return v;
};

// TODO : legalEntitiesUuid is nullable but should not

export class QueryOptionsFactory {
  static legalEntity(legalEntityUuid: string, isAdmin: boolean = false) {
    const legalEntityQueryKeys = (legalEntityUuid: string, isAdmin: boolean = false): string[] => {
      if (isAdmin) return ["admin", "legal-entity", legalEntityUuid];
      return ["legal-entity", legalEntityUuid];
    };

    return queryOptions<companyDetails | null, null, companyDetails | null>({
      queryKey: legalEntityQueryKeys(legalEntityUuid, isAdmin),
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: false,
      staleTime: 10 * 60 * 1000
    });
  }

  static legalEntities(isAdmin: boolean = false) {
    const legalEntitiesQueryKeys = (isAdmin: boolean = false): string[] => {
      if (isAdmin) return ["admin", "legal-entities"];
      return ["legal-entities"];
    };

    return queryOptions<LegalEntityFrontDto[], null, LegalEntityFrontDto[]>({
      queryKey: legalEntitiesQueryKeys(isAdmin),
      retryOnMount: false,
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: 10 * 60 * 1000
    });
  }

  static aliasMobile(legalIdentityUuid: string | null) {
    return queryOptions<AliasMobileSchema[]>({
      queryKey: legalIdentityUuid ? ["alias-mobile", legalIdentityUuid] : ["alias-mobile"],
      staleTime: 10 * 60 * 1000
    });
  }

  static aliasMail(legalIdentityUuid: string | null) {
    return queryOptions<AliasMailSchema[]>({
      queryKey: legalIdentityUuid ? ["alias-mail", legalIdentityUuid] : ["alias-mail"],
      staleTime: 10 * 60 * 1000
    });
  }

  static links(legalIdentityUuid: string | null) {
    return queryOptions<LinkSchemas[]>({
      queryKey: ["legal-entity", legalIdentityUuid ?? "", "links"],
      staleTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnMount: false
    });
  }

  static paymentInformations(uuid: string) {
    return queryOptions<CompanyPaymentInformations, ApiError, CompanyPaymentInformations>({
      queryKey: ["payment-informations", uuid],
      staleTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: false
    });
  }

  static identityDocumentTypeAll() {
    return queryOptions<IdentityDocumentTypeV2[]>({
      queryKey: ["identity-documet-type", "all"],
      select: (data: IdentityDocumentTypeV2[]) => pushOtherToEnd(data, "typeEng"),
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      gcTime: Infinity,
      staleTime: Infinity
    });
  }
}
